import {useEffect, useMemo, useState} from 'react';

import {merchantService} from '@/services/merchants';
import type {BusinessRFIDetail} from '@/services/rfiService';
import {rfiService} from '@/services/rfiService';
import {userService} from '@/services/userService';
import type {BusinessRFINote} from '@/types';
import {BusinessRFIActorType} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

/**
 * Loads a single RFI and resolves the human-readable names needed by the logs view:
 * - admin actors  -> admin profile name (userService.getUser)
 * - business actors / the "For" field -> the business name (merchantService.getBusinessDetails)
 * `isLoading` stays true until the RFI AND all names are resolved, so the UI never flashes
 * raw ids or "ADMIN"/"BUSINESS" placeholders.
 */
export const useBusinessRFILog = (rfiId?: string) => {
  const [rfi, setRfi] = useState<BusinessRFIDetail | null>(null);
  const [isLoadingRfi, setIsLoadingRfi] = useState<boolean>(true);
  const [isResolving, setIsResolving] = useState<boolean>(true);
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const [businessName, setBusinessName] = useState<string>('');

  // 1. Fetch the RFI.
  useEffect(() => {
    let active = true;
    const fetchRFI = async () => {
      if (!rfiId) {
        setIsLoadingRfi(false);
        return;
      }
      try {
        setIsLoadingRfi(true);
        const data = await rfiService.getBusinessRFI(rfiId);
        if (active) {
          setRfi(data);
        }
      } catch (e) {
        showErrorMessage(getErrorMessage(e, 'Error loading RFI logs'));
      } finally {
        if (active) {
          setIsLoadingRfi(false);
        }
      }
    };
    // `fetchRFI` toasts its own failures, so this only fires if that handler
    // itself throws. Log rather than swallow silently.
    fetchRFI().catch(logRejection('Failed to load RFI'));
    return () => {
      active = false;
    };
  }, [rfiId]);

  const notes = useMemo(() => rfi?.notes ?? [], [rfi]);
  const adminIds = useMemo(
    () =>
      Array.from(
        new Set(
          notes
            .filter((n) => n.actorType === BusinessRFIActorType.Admin)
            .map((n) => n.changedBy)
            .filter(Boolean),
        ),
      ),
    [notes],
  );
  const hasBusinessActor = useMemo(
    () => notes.some((n) => n.actorType === BusinessRFIActorType.Business),
    [notes],
  );
  // We need the business name for business actors, and for the "For" field when the RFI
  // is about the business itself (no customer email).
  const needsBusinessName = !!rfi && (hasBusinessActor || !rfi.customerEmail);

  // 2. Resolve admin + business names once the RFI is loaded.
  useEffect(() => {
    let active = true;
    const resolveNames = async () => {
      if (!rfi) {
        return;
      }
      setIsResolving(true);
      try {
        const [adminEntries, resolvedBusinessName] = await Promise.all([
          Promise.all(
            adminIds.map(async (id): Promise<[string, string]> => {
              try {
                const user = await userService.getUser(id);
                return [id, user?.name?.fullName || user?.currentEmail || id];
              } catch {
                return [id, id];
              }
            }),
          ),
          needsBusinessName && rfi.businessId
            ? merchantService
                .getBusinessDetails(rfi.businessId)
                .then((d) => d?.business?.name || '')
                .catch(() => '')
            : Promise.resolve(''),
        ]);
        if (active) {
          setAdminNames(Object.fromEntries(adminEntries));
          setBusinessName(resolvedBusinessName);
        }
      } finally {
        if (active) {
          setIsResolving(false);
        }
      }
    };
    // Every lookup inside `resolveNames` falls back to the raw id, and the
    // function has a finally but no catch, so an unexpected throw would escape
    // unhandled. Names stay unresolved either way; just log it.
    resolveNames().catch(logRejection('Failed to resolve RFI actor names'));
    return () => {
      active = false;
    };
  }, [rfi, adminIds, needsBusinessName]);

  const resolveActor = (note: BusinessRFINote): string => {
    // A processor note's changedBy is the rail's name, not a user id, so it is
    // already the label — and it must not be looked up as a user or fall through
    // to the business name.
    if (note.actorType === BusinessRFIActorType.Processor) {
      return note.changedBy || 'Processor';
    }
    return note.actorType === BusinessRFIActorType.Admin
      ? adminNames[note.changedBy] || 'Admin'
      : businessName || 'Business';
  };

  // "For": the business customer (their email is how the BE identifies them) or the business.
  const forLabel = rfi?.customerEmail || businessName || '—';

  const isLoading = isLoadingRfi || (!!rfi && isResolving);

  return {rfi, isLoading, resolveActor, forLabel};
};

export default useBusinessRFILog;
